import React, { useState, useRef } from 'react';
import { Trash, PencilSimple, ArrowLeft, FilePdf, Upload, Eye, Folder, Camera } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { key: 'ci_recto', label: 'Carte identite (recto)' },
  { key: 'ci_verso', label: 'Carte identite (verso)' },
  { key: 'carte_vitale', label: 'Carte vitale' },
  { key: 'permis_conduire', label: 'Permis de conduire' },
];

export const EmployeeDetail = ({ employee, documents, isAdmin, SalaryEvolutionTable, onBack, onEdit, onDelete, onUploadDoc, onDeleteDoc, showModal, renderEmployeeModal }) => {
  const [photoUrl, setPhotoUrl] = useState(employee?.photo_url || '');
  const fileRef = useRef(null);

  if (!employee) return null;
  const emp = employee;

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/users/${emp.id}/photo`, fd, { withCredentials: true });
      setPhotoUrl(res.data.photo_url);
      toast.success('Photo mise a jour');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload photo');
    }
  };

  return (
    <div data-testid="employee-detail">
      <button className="btn btn-secondary btn-sm" onClick={onBack} style={{ marginBottom: '16px' }} data-testid="back-btn"><ArrowLeft size={14} /> Retour</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ position: 'relative' }}>
            {photoUrl ? (
              <img src={`${API_URL}${photoUrl}`} alt={emp.name} style={{ width: '64px', height: '64px', borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--primary, #002FA7)' }} onError={() => setPhotoUrl('')} />
            ) : (
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: 'var(--primary, #002FA7)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '1.5rem' }}>
                {(emp.first_name || emp.name || '?').charAt(0).toUpperCase()}
              </div>
            )}
            {isAdmin && (
              <button
                data-testid="upload-photo-btn"
                onClick={() => fileRef.current?.click()}
                style={{ position: 'absolute', bottom: '-2px', right: '-2px', width: '24px', height: '24px', borderRadius: '50%', backgroundColor: 'var(--primary, #002FA7)', color: 'white', border: '2px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
              >
                <Camera size={12} weight="bold" />
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />
          </div>
          <div>
            <h1 className="page-title">{emp.first_name || ''} {emp.last_name || emp.name}</h1>
            <p className="page-subtitle">{emp.email} - {emp.job_type || emp.job_title || 'Non renseigne'}</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-secondary" onClick={() => window.open(`${API_URL}/api/users/${emp.id}/export/pdf`, '_blank')} data-testid="export-employee-pdf-btn"><FilePdf size={14} /> Fiche PDF</button>
          <button className="btn btn-secondary" onClick={() => onEdit(emp)} data-testid="edit-employee-btn"><PencilSimple size={14} /> Modifier</button>
          <button className="btn btn-danger" onClick={() => onDelete(emp.id, emp.name)}><Trash size={14} /></button>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        <div className="card">
          <div className="card-header"><h3 className="card-title">Informations personnelles</h3></div>
          <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {[['Nom', emp.last_name], ['Prenom', emp.first_name], ['Date de naissance', emp.birth_date], ['Nationalite', emp.nationality], ['Permis', emp.drivers_license_type], ['N secu', emp.social_security_number], ['RIB', emp.rib]].map(([label, val]) => (
              <div key={label}><span style={{ color: '#5E5F66', fontSize: '0.85rem' }}>{label}</span><div style={{ fontWeight: '600', marginTop: '4px' }}>{val || '-'}</div></div>
            ))}
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3 className="card-title">Contrat & Poste</h3></div>
          <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {[['Poste', emp.job_type || emp.job_title], ['Qualification', emp.qualification], ['Contrat', emp.contract_type], ['Date entree', emp.entry_date], ['Date sortie', emp.exit_date || '-'], ...(emp.contract_type === 'CDD' ? [['CDD debut', emp.cdd_start_date], ['CDD fin', emp.cdd_end_date]] : [])].map(([label, val]) => (
              <div key={label}><span style={{ color: '#5E5F66', fontSize: '0.85rem' }}>{label}</span><div style={{ fontWeight: '600', marginTop: '4px' }}>{val || '-'}</div></div>
            ))}
            <div style={{ gridColumn: 'span 2', display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '8px' }}>
              {[['DPAE', emp.dpae_done], ['Contrat signe', emp.contract_signed], ['Caisse conges', emp.caisse_conges], ['Mutuelle', emp.mutuelle]].map(([l, v]) => (
                <span key={l} style={{ padding: '4px 12px', borderRadius: '4px', fontSize: '0.8rem', fontWeight: '600', backgroundColor: v ? '#E8F5E9' : '#FFEBEE', color: v ? '#2E7D32' : '#C62828' }}>{l}: {v ? 'Oui' : 'Non'}</span>
              ))}
            </div>
            {emp.mutuelle && emp.mutuelle_type && <div style={{ gridColumn: 'span 2' }}><span style={{ color: '#5E5F66', fontSize: '0.85rem' }}>Type mutuelle</span><div style={{ fontWeight: '600', marginTop: '4px' }}>{emp.mutuelle_type}</div></div>}
            <div><span style={{ color: '#5E5F66', fontSize: '0.85rem' }}>Tarif journalier</span><div style={{ fontWeight: '700', marginTop: '4px', fontSize: '1.1rem', color: '#002FA7' }}>{emp.daily_rate ? `${emp.daily_rate} euro/jour` : 'Non defini'}</div></div>
            <div><span style={{ color: '#5E5F66', fontSize: '0.85rem' }}>Taux horaire brut</span><div style={{ fontWeight: '700', marginTop: '4px', fontSize: '1.1rem', color: '#002FA7' }}>{emp.hourly_rate ? `${emp.hourly_rate} euro/h` : 'Non defini'}</div></div>
          </div>
        </div>
      </div>
      {isAdmin && <SalaryEvolutionTable userId={emp.id} />}
      <div className="card" style={{ marginTop: '24px' }}>
        <div className="card-header"><h3 className="card-title">Documents obligatoires</h3></div>
        <div style={{ padding: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
            {DOC_TYPES.map(dt => {
              const doc = documents.find(d => d.doc_type === dt.key);
              return (
                <div key={dt.key} style={{ border: '1px solid #E2E2E6', borderRadius: '6px', padding: '16px', textAlign: 'center' }}>
                  <Folder size={28} style={{ color: doc ? '#2E7D32' : '#5E5F66', marginBottom: '8px' }} />
                  <div style={{ fontSize: '0.8rem', fontWeight: '600', marginBottom: '8px' }}>{dt.label}</div>
                  {doc ? (
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                      <a href={`${API_URL}/api/users/documents/file/${doc.filename}`} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm"><Eye size={12} /></a>
                      <button className="btn btn-danger btn-sm" onClick={() => onDeleteDoc(emp.id, doc.id)}><Trash size={12} /></button>
                    </div>
                  ) : (
                    <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                      <Upload size={12} /> Televerser
                      <input type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={(e) => { if (e.target.files[0]) onUploadDoc(emp.id, dt.key, e.target.files[0]); }} />
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {showModal && renderEmployeeModal()}
    </div>
  );
};
